import { useEffect } from 'react';
import { ASK_TYPE_QS } from '../data/heroFeed';
import { capabilityById } from '../data/heroCapabilities';
import { panelRowHtml } from '../data/heroPanels';
import { getCapability, subscribeCapability } from './heroCapabilityStore';

/* crisp vector monitor used as the per-team hardware icon */
const MACHINE_SVG =
  '<svg viewBox="0 0 24 22" aria-hidden="true">' +
  '<rect class="scr" x="1.5" y="1.5" width="21" height="13.5" rx="2.4"/>' +
  '<rect class="mbar" x="1.5" y="1.5" width="21" height="3" rx="2.4"/>' +
  '<path class="bars" d="M5 8.3h9M5 11.2h5.5"/>' +
  '<rect class="frame" x="1.5" y="1.5" width="21" height="13.5" rx="2.4"/>' +
  '<path class="stand" d="M12 15.4v2.6"/>' +
  '<path class="base" d="M7.8 19.8h8.4"/>' +
  '</svg>';

/* the org panel: a believable slice of a customer's company */
const TEAMS = [
  { name: 'ENGINEERING', n: 34 },
  { name: 'SALES', n: 21 },
  { name: 'OPS', n: 16 },
  { name: 'DESIGN', n: 12 },
  { name: 'MARKETING', n: 9 },
];
const MAX_PIPS = 7;

/* which team card an insight visibly originates from (by feed tag) */
const TAG_TEAM = {
  PERFORMANCE: 0,
  FOCUS: 0,
  SECURITY: 0,
  EMAIL: 1,
  CRM: 1,
  MEETINGS: 2,
  HRMS: 2,
  RESOURCES: 2,
  'AI RADAR': 3,
  'TEAM WIN': 3,
  'EARLY WARNING': 4,
  'FLIGHT RISK': 4,
};

function hexToRgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

export default function useHeroSimulation() {
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stackEl = document.getElementById('teamStack');
    const countEl = document.getElementById('agentCount');
    const feedEl = document.getElementById('feed');
    if (!stackEl || !feedEl) return undefined;

    const timers = [];
    const intervals = [];
    const cleanups = [];

    /* ---- build team cards ---- */
    const cards = TEAMS.map((t) => {
      const card = document.createElement('div');
      card.className = 'team-card';
      const shown = Math.min(t.n, MAX_PIPS);
      const more = t.n - shown;
      card.innerHTML =
        `<div class="tc-head"><span class="tc-ico">${MACHINE_SVG}</span>` +
        `<span class="tc-name">${t.name}</span>` +
        `<span class="tc-count"><i></i><b>0</b>&nbsp;machines</span></div>` +
        `<div class="tc-pips">${`<span class="pipm">${MACHINE_SVG}</span>`.repeat(shown)}${more > 0 ? `<span class="tc-more">+${more}</span>` : ''}</div>`;
      stackEl.appendChild(card);
      return { ...t, el: card, pips: [...card.querySelectorAll('.pipm')], countB: card.querySelector('.tc-count b') };
    });

    const fmt = (n) => n.toLocaleString('en-US');
    let waveTimer = null;

    /* ---- boot sequence: cards slide in, screens turn on, org counter 10 -> 10,000, settle once ---- */
    function boot() {
      const T = 5200;
      const t0 = Date.now();
      clearInterval(waveTimer);
      waveTimer = setInterval(() => {
        const p = Math.min(1, (Date.now() - t0) / T);
        const e = 1 - Math.pow(1 - p, 3);
        countEl.textContent = fmt(Math.max(10, Math.round(e * 10000)));
        if (p >= 1) {
          clearInterval(waveTimer);
          const ac = countEl.closest('.ap-count');
          if (ac) {
            ac.classList.add('settled');
            timers.push(setTimeout(() => ac.classList.remove('settled'), 900));
          }
        }
      }, 90);
      cards.forEach((c, i) => {
        timers.push(
          setTimeout(() => {
            c.el.classList.add('in');
            c.pips.forEach((pip, j) => {
              timers.push(
                setTimeout(() => {
                  pip.classList.add('on');
                  c.countB.textContent = Math.round(((j + 1) / c.pips.length) * c.n);
                }, 130 + j * 42),
              );
            });
          }, 220 + i * 240),
        );
      });
    }

    if (reduce) {
      cards.forEach((c) => {
        c.el.classList.add('in');
        c.pips.forEach((p) => p.classList.add('on'));
        c.countB.textContent = c.n;
      });
      countEl.textContent = '10,000';
    } else {
      boot();
    }

    let fi = 0;
    let paused = false;
    const AGES = ['now', '1m', '2m', '4m', '7m', '11m', '14m', '19m', '23m', '28m', '34m', '41m'];
    /* keep a scrollable history rather than a fixed 6-item window */
    const FEED_MAX = 12;
    let rafFeed = 0;
    /* set once the drift loop exists; lets prependCard keep the drift in sync */
    let feedShift = null;

    /* A row's shape depends on the capability on show: a chat exchange for Ask
       TeamGrid, a score bar for Productivity Scoring, an hour strip for Hourly
       Breakdown, and so on. `kind` selects the archetype. */
    function card(it, kind = 'insight') {
      const el = document.createElement('div');
      el.className = `fcard prow-${kind}`;
      el.innerHTML = panelRowHtml(kind, it);
      return el;
    }

    const flowLayer = document.getElementById('flowLayer');
    const flowSvg = document.getElementById('flowSvg');
    const brainBig = document.getElementById('brainBig');

    /* right-edge anchor of a team card, in flow-layer coordinates */
    function cardAnchor(c) {
      const r = flowLayer.getBoundingClientRect();
      const b = c.el.getBoundingClientRect();
      return { x: b.right - r.left - 4, y: b.top - r.top + b.height / 2 };
    }

    function pt(el) {
      const r = flowLayer.getBoundingClientRect();
      const c = el.getBoundingClientRect();
      return { x: c.left - r.left + c.width / 2, y: c.top - r.top + c.height / 2 };
    }

    function drawTrails() {
      if (!flowLayer?.offsetParent) return;
      const r = flowLayer.getBoundingClientRect();
      flowSvg.setAttribute('viewBox', `0 0 ${r.width} ${r.height}`);
      const paths = flowSvg.querySelectorAll('path');
      const bp = pt(brainBig);
      cards.forEach((c, i) => {
        if (!paths[i]) return;
        const a = cardAnchor(c);
        const mx = (a.x + bp.x) / 2;
        paths[i].setAttribute('d', `M${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${bp.y}, ${bp.x} ${bp.y}`);
      });
      const f = feedEl.getBoundingClientRect();
      const fx = f.left - r.left + 14;
      const ft = f.top - r.top;
      [0.16, 0.5].forEach((k, i) => {
        const p = paths[cards.length + i];
        if (!p) return;
        const ey = ft + 60 + 160 * k * 2;
        const mx = (bp.x + fx) / 2;
        p.setAttribute('d', `M${bp.x} ${bp.y} C ${mx} ${bp.y}, ${mx} ${ey}, ${fx} ${ey}`);
      });
    }

    drawTrails();
    timers.push(setTimeout(drawTrails, 500));
    timers.push(setTimeout(drawTrails, 1600));
    const onResize = () => drawTrails();
    window.addEventListener('resize', onResize);
    cleanups.push(() => window.removeEventListener('resize', onResize));

    function pulseBrain(color) {
      if (reduce || !brainBig) return;
      brainBig.animate(
        [
          { transform: 'translate(-50%,-50%) scale(1)' },
          { transform: 'translate(-50%,-50%) scale(1.09)' },
          { transform: 'translate(-50%,-50%) scale(1)' },
        ],
        { duration: 360, easing: 'ease-out' },
      );
      if (!flowLayer?.offsetParent) return;
      const b = pt(brainBig);
      const ring = document.createElement('span');
      ring.className = 'brain-ring';
      if (color) ring.style.borderColor = color;
      ring.style.left = `${b.x - 43}px`;
      ring.style.top = `${b.y - 43}px`;
      flowLayer.appendChild(ring);
      ring.animate(
        [
          { transform: 'scale(.55)', opacity: 0.85 },
          { transform: 'scale(1.75)', opacity: 0 },
        ],
        { duration: 640, easing: 'cubic-bezier(.2,.7,.3,1)' },
      ).onfinish = () => ring.remove();
    }

    /* one packet flying a gentle arc between two points (offset-path when supported) */
    const hasOffsetPath =
      typeof CSS !== 'undefined' && CSS.supports('offset-path', 'path("M0 0 L1 1")');
    function flyBlip(x0, y0, x1, y1, { color = null, dur = 700, size = 7, done = null } = {}) {
      if (reduce || !flowLayer?.offsetParent) {
        done?.();
        return;
      }
      const b = document.createElement('span');
      b.className = 'fblip';
      b.style.width = `${size}px`;
      b.style.height = `${size}px`;
      if (color) {
        b.style.background = color;
        b.style.boxShadow = `0 0 10px ${hexToRgba(color, 0.85)}`;
      }
      const dx = x1 - x0;
      const dy = y1 - y0;
      const len = Math.hypot(dx, dy) || 1;
      const arc = Math.min(34, len * 0.22);
      const px = (-dy / len) * arc;
      const py = (dx / len) * arc;
      const mx = (x0 + x1) / 2 + px;
      const my = (y0 + y1) / 2 + py;
      let anim;
      if (hasOffsetPath) {
        b.style.left = '0px';
        b.style.top = '0px';
        b.style.offsetPath = `path("M ${x0} ${y0} Q ${mx} ${my} ${x1} ${y1}")`;
        b.style.offsetRotate = '0deg';
        flowLayer.appendChild(b);
        anim = b.animate(
          [
            { offsetDistance: '0%', opacity: 0 },
            { opacity: 1, offset: 0.18 },
            { offsetDistance: '100%', opacity: 0.95 },
          ],
          { duration: dur, easing: 'cubic-bezier(.45,0,.55,1)' },
        );
      } else {
        b.style.left = `${x0 - size / 2}px`;
        b.style.top = `${y0 - size / 2}px`;
        flowLayer.appendChild(b);
        anim = b.animate(
          [
            { transform: 'translate(0,0)', opacity: 0 },
            { opacity: 1, offset: 0.18 },
            { transform: `translate(${mx - x0}px,${my - y0}px)`, opacity: 1, offset: 0.5 },
            { transform: `translate(${dx}px,${dy}px)`, opacity: 0.95 },
          ],
          { duration: dur, easing: 'cubic-bezier(.45,0,.55,1)' },
        );
      }
      anim.onfinish = () => {
        b.remove();
        done?.();
      };
    }

    /* ---- status sweep: an eased scanner pass — bright line + wake, cards highlight
       while the line is inside them, fade out at the bottom, pause, repeat ---- */
    const scanEl = document.getElementById('scanBar');
    let rafId = 0;
    if (!reduce && scanEl) {
      const SWEEP = 3600;
      const FADE = 260;
      const HOLD = 1500;
      const CYCLE = SWEEP + FADE + HOLD;
      const t0 = performance.now();
      let lastFlick = 0;
      const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
      const frame = (now) => {
        rafId = requestAnimationFrame(frame);
        if (!stackEl.offsetParent || document.hidden) return;
        const tc = (now - t0) % CYCLE;
        const barH = scanEl.offsetHeight;
        const H = stackEl.offsetHeight - barH;
        let y = 0;
        let op = 0;
        if (tc < SWEEP) {
          y = ease(tc / SWEEP) * H;
          op = Math.min(1, tc / 220);
        } else if (tc < SWEEP + FADE) {
          y = H;
          op = 1 - (tc - SWEEP) / FADE;
        }
        scanEl.style.transform = `translateY(${y}px)`;
        scanEl.style.opacity = op;
        const lineY = y + barH - 7;
        cards.forEach((k) => {
          const inside =
            op > 0.5 && lineY >= k.el.offsetTop && lineY <= k.el.offsetTop + k.el.offsetHeight;
          k.el.classList.toggle('scanning', inside);
        });
        if (tc >= SWEEP || now - lastFlick < 230) return;
        const c = cards.find(
          (k) => lineY >= k.el.offsetTop && lineY <= k.el.offsetTop + k.el.offsetHeight,
        );
        if (!c) return;
        lastFlick = now;
        const on = c.pips.filter((p) => p.classList.contains('on'));
        for (let i = 0; i < 2 && on.length; i++) {
          const p = on[(Math.random() * on.length) | 0];
          p.classList.add('hotp');
          timers.push(setTimeout(() => p.classList.remove('hotp'), 420));
        }
      };
      rafId = requestAnimationFrame(frame);
      cleanups.push(() => cancelAnimationFrame(rafId));
    }

    /* ambient telemetry: teams quietly stream signals to the brain */
    if (!reduce) {
      intervals.push(
        setInterval(() => {
          if (paused || document.hidden || !flowLayer?.offsetParent) return;
          const c = cards[(Math.random() * cards.length) | 0];
          const a = cardAnchor(c);
          const bp = pt(brainBig);
          flyBlip(a.x, a.y, bp.x, bp.y, { dur: 820, size: 5 });
        }, 1150),
      );
    }

    /* ---- fleet tooltip: hover a team, see its machine mix ---- */
    const tipEl = document.getElementById('fleetTip');
    if (tipEl) {
      cards.forEach((c) => {
        const mac = Math.round(c.n * 0.55);
        const win = Math.round(c.n * 0.34);
        const lnx = Math.max(0, c.n - mac - win);
        const over = () => {
          tipEl.innerHTML = `<b>${c.name}</b><span>${mac} macOS · ${win} Windows${lnx ? ` · ${lnx} Linux` : ''}</span>`;
          tipEl.classList.add('show');
          const ty = c.el.offsetTop - tipEl.offsetHeight - 6;
          tipEl.style.left = '10px';
          tipEl.style.top = `${ty < 2 ? c.el.offsetTop + c.el.offsetHeight + 6 : ty}px`;
        };
        const out = () => tipEl.classList.remove('show');
        c.el.addEventListener('mouseenter', over);
        c.el.addEventListener('mouseleave', out);
        cleanups.push(() => {
          c.el.removeEventListener('mouseenter', over);
          c.el.removeEventListener('mouseleave', out);
        });
      });
    }

    /* ---- insight choreography: team card flashes -> packet -> brain -> packet -> feed card ---- */
    function prependCard(it, color, kind) {
      const el = card(it, kind || capabilityById(getCapability()).kind);
      if (color) {
        el.style.setProperty('--fc-wash', hexToRgba(color, 0.09));
        el.classList.add('fresh');
      }
      /* if the reader has scrolled down into the history, keep their position
         instead of yanking them to the top when a new insight lands */
      const atTop = feedEl.scrollTop < 12;
      const beforeH = feedEl.scrollHeight;
      feedEl.prepend(el);
      while (feedEl.children.length > FEED_MAX) feedEl.lastElementChild.remove();
      [...feedEl.children].forEach((c, i) => {
        const a = c.querySelector('.page');
        if (a) a.textContent = AGES[i] || `${Math.min(59, 14 + (i - AGES.length) * 6)}m`;
      });
      const grew = feedEl.scrollHeight - beforeH;
      if (atTop) {
        feedEl.scrollTop = 0;
      } else {
        feedEl.scrollTop += grew;
        feedShift?.(grew); // keep the auto-drift anchored to the same card
      }
    }

    function pushInsight() {
      if (paused || document.hidden) return;
      /* Insights come from whichever capability the chip row is showing, so the
         right-hand feed is always evidence for the chip that's lit. */
      const cap = capabilityById(getCapability());
      const pool = cap.items;
      const it = pool[fi++ % pool.length];
      const color = it.c || '#007A6E';
      if (reduce || !flowLayer?.offsetParent) {
        if (cap.stream) prependCard(it, color, cap.kind);
        return;
      }
      const team = cards[cap.team ?? TAG_TEAM[it.tag] ?? (Math.random() * cards.length) | 0];
      team.el.style.setProperty('--tc', color);
      team.el.style.setProperty('--tc-soft', hexToRgba(color, 0.16));
      team.el.classList.add('alert');
      timers.push(setTimeout(() => team.el.classList.remove('alert'), 1050));
      const a = cardAnchor(team);
      const bp = pt(brainBig);
      const fr = feedEl.getBoundingClientRect();
      const lr = flowLayer.getBoundingClientRect();
      const fx = fr.left - lr.left + 12;
      const fy = fr.top - lr.top + 26 + Math.random() * 24;
      timers.push(
        setTimeout(() => {
          flyBlip(a.x, a.y, bp.x, bp.y, {
            color,
            dur: 640,
            done: () => {
              pulseBrain(color);
              flyBlip(bp.x, bp.y, fx, fy, {
                color,
                dur: 500,
                size: 6,
                /* A packet is ~1.3s in flight. If the chip row advanced while it
                   was travelling, this insight belongs to the panel that just
                   left — drop it rather than render one capability's data in
                   another's layout (which showed up as an "undefined" row). */
                done: () => {
                  if (getCapability() !== cap.id) return;
                  if (cap.stream) {
                    prependCard(it, color, cap.kind);
                    return;
                  }
                  /* Score lists, pipelines, goals and the like are current
                     state, not an event log — prepending duplicated rows that
                     were already on screen. Refresh one in place instead, so
                     the panel still reads as live. */
                  const rows = feedEl.children;
                  if (!rows.length) return;
                  const row = rows[(Math.random() * Math.min(rows.length, 4)) | 0];
                  row.style.setProperty('--fc-wash', hexToRgba(color, 0.1));
                  row.classList.remove('refreshed');
                  void row.offsetWidth;
                  row.classList.add('refreshed');
                },
              });
            },
          });
        }, 160),
      );
    }

    /* Seeded by showCapability() below, from whichever capability the chip row
       starts on — a generic pre-fill would flash the wrong panel first. */
    feedEl.scrollTop = 0;
    /* Capability switch: cross-fade the feed, then refill it from the new
       capability so the panel visibly answers the chip. */
    function showCapability(id, animate = true) {
      const cap = capabilityById(id);
      const paint = () => {
        feedEl.innerHTML = '';
        fi = 0;
        cap.items.slice(0, 8).forEach((it, i) => {
          const el = card(it, cap.kind);
          const a = el.querySelector('.page');
          if (a) a.textContent = AGES[i] || 'now';
          el.style.setProperty('--fc-wash', hexToRgba(it.c || '#007A6E', 0.07));
          feedEl.appendChild(el);
        });
        fi = Math.min(8, cap.items.length);
        feedEl.scrollTop = 0;
        feedEl.classList.remove('swapping');
      };
      if (!animate || reduce) { paint(); return; }
      feedEl.classList.add('swapping');
      timers.push(setTimeout(paint, 190));
    }

    showCapability(getCapability(), false);
    const unsubCapability = subscribeCapability((id) => showCapability(id));

    if (!reduce) intervals.push(setInterval(pushInsight, 3200));

    const onEnter = () => {
      if (!document.getElementById('feedPause')?.classList.contains('is-paused')) paused = true;
    };
    const onLeave = () => {
      if (!document.getElementById('feedPause')?.classList.contains('is-paused')) paused = false;
    };
    feedEl.addEventListener('mouseenter', onEnter);
    feedEl.addEventListener('mouseleave', onLeave);
    cleanups.push(() => {
      feedEl.removeEventListener('mouseenter', onEnter);
      feedEl.removeEventListener('mouseleave', onLeave);
    });

    const pbtn = document.getElementById('feedPause');
    const onPause = () => {
      const on = pbtn.classList.toggle('is-paused');
      paused = on;
      pbtn.textContent = on ? 'RESUME' : 'PAUSE';
    };
    pbtn?.addEventListener('click', onPause);
    cleanups.push(() => pbtn?.removeEventListener('click', onPause));

    /* ---- the feed drifts on its own, like a live wire.
       Pauses on hover / PAUSE, holds a beat at each end, and hands control
       straight back to the reader the moment they scroll it themselves. ---- */
    if (!reduce) {
      let pos = 0;
      let hold = 700;
      let last = performance.now();
      let self = false;
      const SPEED = 15; // px per second — slow enough to read past
      const onUserScroll = () => {
        if (self) return;
        pos = feedEl.scrollTop;
        hold = 2200; // let them read before drifting again
      };
      feedEl.addEventListener('wheel', onUserScroll, { passive: true });
      feedEl.addEventListener('touchmove', onUserScroll, { passive: true });
      cleanups.push(() => {
        feedEl.removeEventListener('wheel', onUserScroll);
        feedEl.removeEventListener('touchmove', onUserScroll);
      });
      feedShift = (d) => {
        pos = Math.max(0, pos + d);
      };
      const drift = (now) => {
        rafFeed = requestAnimationFrame(drift);
        const dt = Math.min(80, now - last);
        last = now;
        if (paused || document.hidden || !feedEl.offsetParent) return;
        const max = feedEl.scrollHeight - feedEl.clientHeight;
        if (max <= 4) return;
        if (hold > 0) {
          hold -= dt;
          return;
        }
        pos += (SPEED * dt) / 1000;
        if (pos >= max) {
          pos = max;
          hold = 1600;
          self = true;
          feedEl.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(() => {
            pos = 0;
            self = false;
          }, 620);
          return;
        }
        self = true;
        feedEl.scrollTop = pos;
        self = false;
      };
      rafFeed = requestAnimationFrame(drift);
      cleanups.push(() => cancelAnimationFrame(rafFeed));
    }


    /* ask typewriter */
    const ask = document.getElementById('askType');
    if (ask) {
      if (reduce) {
        ask.textContent = ASK_TYPE_QS[0];
      } else {
        let qi = 0;
        let ci = 0;
        let del = false;
        let typeTimer;
        const type = () => {
          const q = ASK_TYPE_QS[qi % ASK_TYPE_QS.length];
          ask.textContent = q.slice(0, ci);
          if (!del) {
            ci++;
            if (ci > q.length) {
              del = true;
              typeTimer = setTimeout(type, 1900);
              timers.push(typeTimer);
              return;
            }
          } else {
            ci--;
            if (ci === 0) {
              del = false;
              qi++;
            }
          }
          typeTimer = setTimeout(type, del ? 16 : 44);
          timers.push(typeTimer);
        };
        type();
      }
    }

    return () => {
      clearInterval(waveTimer);
      intervals.forEach(clearInterval);
      timers.forEach(clearTimeout);
      cleanups.forEach((fn) => fn());
      cards.forEach((c) => c.el.remove());
      unsubCapability();
    };
  }, []);
}
